import { Box } from '@mui/material'
import DOMPurify from 'dompurify'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { escapeUnsupportedHtmlTags } from '../utils/htmlTags'

type MarkdownContentProps = {
  markdown?: string | null
  emptyFallback?: string
}

export function MarkdownContent({ markdown, emptyFallback }: MarkdownContentProps) {
  const content = DOMPurify.sanitize(escapeUnsupportedHtmlTags((markdown || '').trim()), {
    ADD_ATTR: ['height', 'width'],
    ALLOWED_TAGS: ['a', 'blockquote', 'br', 'code', 'em', 'h1', 'h2', 'h3', 'h4', 'hr', 'img', 'li', 'ol', 'p', 'pre', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul'],
    USE_PROFILES: { html: true },
  })

  if (!content) {
    return emptyFallback ? (
      <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{emptyFallback}</Box>
    ) : null
  }

  return (
    <Box
      sx={{
        color: '#1f2937',
        fontSize: '0.95rem',
        lineHeight: 1.7,
        '& h1, & h2, & h3, & h4': {
          color: '#0f172a',
          fontWeight: 700,
          lineHeight: 1.3,
          mt: 2.5,
          mb: 1.25,
        },
        '& h1': { fontSize: '1.7rem' },
        '& h2': { fontSize: '1.35rem' },
        '& h3': { fontSize: '1.15rem' },
        '& hr': {
          border: 0,
          borderTop: '1px solid #d8e0ea',
          my: 3,
        },
        '& p': { my: 1.25 },
        '& ul, & ol': { pl: 3, my: 1.25 },
        '& li': { mb: 0.5 },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          my: 2,
          fontSize: '0.92rem',
        },
        '& th, & td': {
          border: '1px solid #d8e0ea',
          px: 1.25,
          py: 0.9,
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& th': {
          backgroundColor: '#f8fafc',
          color: '#0f172a',
          fontWeight: 700,
        },
        '& a': {
          color: '#0f4c81',
          textDecorationColor: 'rgba(15, 76, 129, 0.35)',
          fontWeight: 600,
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          backgroundColor: '#f1f5f9',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        },
        '& pre': {
          overflowX: 'auto',
          backgroundColor: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 2,
          px: 2,
          py: 1.5,
        },
        '& pre code': {
          backgroundColor: 'transparent',
          color: 'inherit',
          padding: 0,
        },
        '& img': {
          display: 'block',
          maxWidth: '100%',
          borderRadius: 0,
          my: 1,
          boxShadow: 'none',
        },
        '& blockquote': {
          borderLeft: '4px solid #cbd5e1',
          color: '#475569',
          my: 2,
          pl: 2,
          ml: 0,
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          img: ({ height, src, alt, width }) => (
            <img
              alt={alt || ''}
              height={height}
              src={src || ''}
              width={width}
              style={{
                height: height ? `${height}px` : 'auto',
                maxWidth: '100%',
                width: width ? `${width}px` : undefined,
              }}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  )
}
